import React, { useState, useEffect } from 'react';
import {
  TableContainer, Table, Thead, Tbody, Tr, Th, Td,
  Flex, HStack, Box,
  Button, Input, Checkbox, FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import type { Employee } from '../../contexts/types';

/**
 * 担当者一覧コンポーネント引数
 */
interface AssignSelectorProps {
  /** 担当者一覧情報（従業員情報） */
  employees: Employee[],
  /** 前回選択担当者の従業員IDリスト */
  prevSelectedEmployeeIds: string[],
  /** 非活性従業員の従業員IDリスト */
  disabledEmployeeIds: string[],
  /** OKボタン押下処理 */
  onOk: (employees: Employee[]) => void,
  /** エラー情報 */
  error: { isError: boolean, errorMessage: string } | null,
  /** 最大選択人数（0 = 無制限、デフォルト: 0） */
  maxAssignees?: number,
}

/**
 * 担当者設定画面
 */
const AssignSelector: React.FC<AssignSelectorProps> = ({ employees, prevSelectedEmployeeIds, disabledEmployeeIds, onOk, error, maxAssignees = 0 }) => {
  /** 翻訳 */
  const { t } = useTranslation();
  /** フィルター文字列 */
  const [filter, setFilter] = useState<string>('');

  /** 選択中の従業員ID */
  //const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>(prevSelectedEmployeeIds);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([
    ...new Set([
      ...prevSelectedEmployeeIds,
      ...disabledEmployeeIds.filter(id => prevSelectedEmployeeIds.includes(id)) // この確認区分でDB保存済みの従業員のみ
    ])
  ]);

  /**
   * propsが変更された時に選択状態を同期する（キャンセル時の復元対応）
   */
  useEffect(() => {
    setSelectedEmployeeIds([
      ...new Set([
        ...prevSelectedEmployeeIds,
        ...disabledEmployeeIds.filter(id => prevSelectedEmployeeIds.includes(id))
      ])
    ]);
  }, [prevSelectedEmployeeIds, disabledEmployeeIds]);

  /**
   * この確認区分で選択されていて、かつDB保存済みの従業員のみがdisabled
   * （他の確認区分で保存された担当者はdisabledにしない）
   */
  const relevantDisabledIds = disabledEmployeeIds.filter(id =>
    prevSelectedEmployeeIds.includes(id)
  );

  /**
   * フィルター後の従業員一覧
   * 検索文字列が空の場合は空配列を返す（検索してから表示する方式）
   */
  const filteredEmployees = (() => {
    // 検索文字列が空の場合は空配列を返す
    if (!filter.trim()) {
      return [];
    }

    // 入力されたフィルター条件をスペースで分割
    const filterTerms = filter.trim().toLowerCase().split(' ');

    // それぞれの条件が社員名と部署名に一致するかチェック
    return employees.filter((employee) =>
      filterTerms.every((term) =>
        (employee.employeeName ?? '').toLowerCase().includes(term) ||
        (employee.divisionName ?? '').toLowerCase().includes(term)
      )
    );
  })();

  /**
   * フィルターされている一覧内で選択されている従業員ID
   */
  const filteredSelectedEmployeeIds = selectedEmployeeIds.filter((employeeId) => filteredEmployees.some(fe => fe.employeeId === employeeId));

  /** 全選択状態 */
  //const isAllSelect = () => selectedEmployeeIds.length > 0 && filteredEmployees.every(fe => selectedEmployeeIds.some(selectedEmployeeId => selectedEmployeeId === fe.employeeId));
  const isAllSelect = () =>
    filteredEmployees.every(
      (fe) =>
        selectedEmployeeIds.includes(fe.employeeId ?? '') ||
        relevantDisabledIds.includes(fe.employeeId ?? '')
    );

  /**
   * 行を選択/解除する処理
   * @param employeeId 操作対象の従業員ID
   */
  const handleRowSelect = (employeeId: string | null | undefined) => {
    employeeId = employeeId ?? '';

    // relevantDisabledIds に含まれている場合操作しない
    if (relevantDisabledIds.includes(employeeId)) {
      return;
    }

    setSelectedEmployeeIds((prevSelected) => {
      // 解除（配列から除外）
      if (prevSelected.includes(employeeId)) {
        return prevSelected.filter((id) => id !== employeeId);
      }

      // maxAssignees制限: 上限に達している場合は追加しない
      if (maxAssignees > 0 && prevSelected.length >= maxAssignees) {
        return prevSelected;
      }

      // 選択（配列へ追加）
      return [...prevSelected, employeeId];
    });
  };

  /**
   * 全選択/解除する処理
   */
  const handleSelectAll = (checked: boolean) => {
    setSelectedEmployeeIds((prevSelected) => {
      if (!checked) {
        // フィルターされているもののみ解除（ただし、非活性従業員は保持）
        return prevSelected.filter((id) =>
          !filteredEmployees.some(e => e.employeeId === id) ||
          relevantDisabledIds.includes(id)
        );
      } else {
        // フィルターされている従業員をすべて選択（非活性従業員は強制追加）
        const merged = [
          ...new Set([
            ...prevSelected,
            ...filteredEmployees.map((fe) => fe.employeeId ?? ''),
          ]),
        ];
        // maxAssignees制限: 上限がある場合は先頭N件に切り詰め
        if (maxAssignees > 0 && merged.length > maxAssignees) {
          return merged.slice(0, maxAssignees);
        }
        return merged;
      }
    });
  };

  /**
   * フィルター文字列を空白にする。
   */
  const handleClear = () => setFilter('');

  /**
 * 本ポップアップを閉じ、結果を呼び出し元に返す。
 * 全従業員リストから選択された従業員を返す（フィルター状態に関係なく）
 */
  const handleOk = () => {
    const result = employees.filter(emp => selectedEmployeeIds.includes(emp.employeeId ?? ''));
    onOk(result);
  };
  return (
    <>
      <HStack mb={3}>
        <Input autoFocus placeholder={t('common.placeholder.employeeSearch')} value={filter} onChange={(e) => { setFilter(e.target.value) }} />
        <Button colorScheme="red" onClick={handleClear}>{t('common.button.clear')}</Button>
      </HStack>
      <FormControl isInvalid={error?.isError ?? false} mb={2}>
        <FormErrorMessage>{error?.errorMessage}</FormErrorMessage>
      </FormControl>
      <Box height="calc(90vh - 305px)" >
        {!filter.trim() ? (
          <Box textAlign="center" py={10} color="gray.500">
            {t('features.Approve.assignment.message.searchEmployeePrompt')}
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <Box textAlign="center" py={10} color="gray.500">
            {t('common.message.noEmployeeFound')}
          </Box>
        ) : (
          <TableContainer border="0px solid" borderColor="gray.200" width="100%" height="100%" overflowY="auto">
            <Table border="1px solid" borderColor="gray.200" variant="simple" style={{ width: "100%", tableLayout: "fixed" }}>
              <Thead position="sticky" top={0} boxShadow={`0 -1px 0 #EEEEEE`} zIndex={1} height={42}>
                <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" onClick={maxAssignees === 1 ? undefined : () => handleSelectAll(!isAllSelect())} cursor={maxAssignees === 1 ? "default" : "pointer"}>
                  <Th width={65}>
                    {maxAssignees !== 1 && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          isChecked={isAllSelect()}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </Box>
                    )}
                  </Th>
                  <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">{t('common.field.departmentName')}</Th>
                  <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width={180}>{t('common.field.employeeName')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredEmployees.map(employee => {  // 従業員情報で繰り返し
                  const isSelected = selectedEmployeeIds.includes(employee.employeeId ?? '');
                  const isRelevantDisabled = relevantDisabledIds.includes(employee.employeeId ?? '');
                  // maxAssignees制限: 上限に達していて未選択の行は非活性
                  const isMaxReached = maxAssignees > 0 && selectedEmployeeIds.length >= maxAssignees && !isSelected;
                  const isDisabled = isRelevantDisabled || isMaxReached;
                  return (
                    <Tr
                      key={employee.employeeId}
                      _hover={{ bg: isDisabled ? undefined : 'blue.200' }} // マウスオーバーで背景色変更
                      bg={filteredSelectedEmployeeIds.includes(employee.employeeId ?? '') ? 'blue.100' : undefined} // 選択行の背景色変更
                      onClick={() => !isDisabled && handleRowSelect(employee.employeeId)}  // 行クリックで選択/解除
                    >
                      <Td>
                        <Checkbox
                          isChecked={selectedEmployeeIds.includes(employee.employeeId ?? '')}
                          isDisabled={isDisabled}
                          onChange={() => handleRowSelect(employee.employeeId)}
                        />
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" title={employee.divisionName} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {employee.divisionName}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" title={employee.employeeName} minWidth={140} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {employee.employeeName}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <Flex alignItems="center" justifyContent="space-between" marginTop={2}>
        <FormLabel fontWeight="bold">{t('common.label.selectedCount')}：{selectedEmployeeIds.length}{maxAssignees > 0 ? ` / ${maxAssignees}` : ""}</FormLabel>
        <Button colorScheme="blue" isDisabled={selectedEmployeeIds.length === 0} onClick={handleOk}>{t('common.button.confirmAction')}</Button>
      </Flex>
    </>
  );
};

export default AssignSelector;

/**
 * 担当者一覧データを取得する。（RDS版）
 * @param request WebAPIリクエスト関数(WebAPI.request)
 * @returns 担当者一覧データ
 */
export const getAssignEmployee = async (request: (payload: any) => Promise<any>) => {

  const apiName = "getUserAll";

  // ユーザー情報取得（RDS）
  const response = await request({
    query: `
        query {
          ${apiName} {
            userId
            userName
            organizationId
            organizationName
            organizationClassId
            parentOrganizationId
            descendantOrganizationClassIds
            positionName
          }
        }`
  });

  if (response && response.data && response.data[apiName]) {
    // 正常 - フィールド名をEmployee型に合わせてマッピング
    return response.data[apiName].map((user: any) => ({
      employeeId: user.userId,
      employeeName: user.userName,
      divisionId: user.organizationId,
      divisionName: user.organizationName,
      organizationClassId: user.organizationClassId,
      parentOrganizationId: user.parentOrganizationId,
      descendantOrganizationClassIds: user.descendantOrganizationClassIds || [],
      positionName: user.positionName || "",
    }));
  } else {
    // 異常
    console.log(`Failed to call API ${apiName}. errors = ${JSON.stringify(response?.errors)}`);
    return null;
  }
};
